import type { FC } from "react";
import {
  type BoxProps,
  Skeleton,
  TabNav,
  TabNavLink,
  type TabNavLinkProps,
} from "@hightouch/htui";
import { RouterLink, useLocation } from "src/router";

export type RouteTab = Pick<
  TabNavLinkProps,
  "statusIndicator" | "isDisabled" | "count" | "tooltip"
> & {
  isHidden?: boolean;
  /** Segment this tab matches on, at the strip's depth. */
  path: string;
  title: string;
  /**
   * Absolute path to link to. Needed when the strip renders inside the route
   * it links from, where a relative `path` would resolve under the active tab.
   */
  to?: string;
  /**
   * Carry the current search string over to this tab. Defaults to true; set it
   * false where the params belong to the tab you are leaving.
   */
  preserveSearch?: boolean;
  /** Explicit query string for tabs that select a view through search params. */
  search?: string;
  /** Override segment matching for tabs spanning routes or query parameters. */
  isActive?: boolean;
  state?: unknown;
};

type Props = BoxProps & {
  isLoading?: boolean;
  tabs: Array<RouteTab>;
  depth?: number;
  collapsible?: boolean;
};

export const useTabState = <T extends RouteTab>(tabs: Array<T>, depth = 3) => {
  const location = useLocation();
  const pathTabs = location.pathname.split("/");

  const visibleTabs = tabs.filter((tab) => !tab.isHidden);

  const activeTabIndex = visibleTabs.findIndex(
    (tab) =>
      tab.isActive ??
      (tab.path === ""
        ? pathTabs.length === depth
        : tab.path === pathTabs[depth]),
  );

  const activeTab = visibleTabs[activeTabIndex];

  return {
    visibleTabs,
    activeTab,
    activeTabIndex,
  };
};

export const RouteTabs: FC<Readonly<Props>> = ({
  tabs,
  depth = 3,
  isLoading = false,
  collapsible = false,
  ...props
}) => {
  const location = useLocation();
  const { visibleTabs, activeTabIndex } = useTabState(tabs, depth);

  return (
    <Skeleton isLoading={isLoading}>
      <TabNav collapsible={collapsible} {...props}>
        {visibleTabs.map((tab, index) => {
          const isActive = index === activeTabIndex;
          return (
            <TabNavLink
              asChild
              key={tab.path === "" ? "index" : tab.path}
              isActive={isActive}
              isDisabled={tab.isDisabled}
              count={tab.count}
              statusIndicator={tab.statusIndicator}
              tooltip={tab.tooltip}
              badgeVariant={isActive ? "info" : "subtle"}
            >
              <RouterLink
                preload={isActive ? undefined : "mount"}
                state={tab.state}
                to={{
                  pathname: tab.to ?? tab.path,
                  // Unless the tab supplies its own query, the active tab keeps its current filters.
                  search:
                    tab.search ??
                    (isActive || tab.preserveSearch !== false
                      ? location.search
                      : ""),
                }}
              >
                {tab.title}
              </RouterLink>
            </TabNavLink>
          );
        })}
      </TabNav>
    </Skeleton>
  );
};
